import React, { useEffect, useState } from "react";
import { db, storage } from "../../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, query, where, orderBy, limit, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../AuthContext";
import TextCustomers from "./TextCustomers";

export default function NotificationsPage() {
  const { user, role, userBars } = useAuth();
  const [selectedBarId, setSelectedBarId] = useState("");
  const [message, setMessage] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const myBars = userBars.filter((bar) =>
    role === "owner" ? bar.ownerId === user.uid : bar.charityId === user.uid
  );

  const loadHistory = async () => {
    setLoading(true);
    const barIds = myBars.map((b) => b.id);
    if (barIds.length === 0) {
      setHistory([]);
      setLoading(false);
      return;
    }
    try {
      const q = query(
        collection(db, "notifications"),
        where("barId", "in", barIds.slice(0, 10)),
        orderBy("createdAt", "desc"),
        limit(20)
      );
      const snap = await getDocs(q);
      setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      setHistory([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBars]);

  const handlePhotoSelect = (file) => {
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedBarId || !message.trim()) return;
    setSending(true);
    setError("");

    try {
      let photoURL = "";
      if (photoFile) {
        const fileRef = ref(storage, `update-photos/${selectedBarId}/${Date.now()}-${photoFile.name}`);
        await uploadBytes(fileRef, photoFile);
        photoURL = await getDownloadURL(fileRef);
      }

      await addDoc(collection(db, "notifications"), {
        barId: selectedBarId,
        message: message.trim(),
        photoURL,
        sentBy: user.uid,
        sentByRole: role,
        createdAt: Timestamp.now(),
      });

      setMessage("");
      setPhotoFile(null);
      setPhotoPreview(null);
      setSent(true);
      setTimeout(() => setSent(false), 2500);
      loadHistory();
    } catch (err) {
      console.error(err);
      setError("Failed to send: " + err.message);
    }

    setSending(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    await deleteDoc(doc(db, "notifications", id));
    loadHistory();
  };

  const barName = (barId) => myBars.find((b) => b.id === barId)?.name || "Unknown Bar";

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    fontSize: "15px",
    boxSizing: "border-box",
  };
  const labelStyle = { display: "block", marginBottom: "8px", fontWeight: 600 };

  if (myBars.length === 0) {
    return (
      <p style={{ color: "#fff" }}>
        You need to claim a bar first before you can send notifications.
      </p>
    );
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "10px" }}>Notifications</h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        Something happening right now? Let customers know — "Bingo starts at 6!" or "Kitchen's closed early tonight."
      </p>

      <form
        onSubmit={handleSend}
        style={{
          background: "#0D0B1A",
          padding: "24px",
          borderRadius: "12px",
          maxWidth: "520px",
          marginBottom: "30px",
        }}
      >
        <label style={labelStyle}>Venue</label>
        <select
          value={selectedBarId}
          onChange={(e) => setSelectedBarId(e.target.value)}
          required
          style={{ ...inputStyle, marginBottom: "18px" }}
        >
          <option value="">Select a bar</option>
          {myBars.map((bar) => (
            <option key={bar.id} value={bar.id}>{bar.name || "Unnamed Bar"}</option>
          ))}
        </select>

        <label style={labelStyle}>Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Bingo tonight, $1,000 coverall, starts at 6! See you there."
          maxLength={200}
          style={{ ...inputStyle, height: "90px", marginBottom: "8px" }}
        />
        <p style={{ fontSize: "15px", color: "#B8B0C8", margin: "0 0 18px", textAlign: "right" }}>
          {message.length}/200
        </p>

        <label style={labelStyle}>Photo, optional</label>
        {photoPreview && (
          <img
            src={photoPreview}
            alt="Preview"
            style={{ width: "100%", maxHeight: "180px", objectFit: "cover", borderRadius: "8px", marginBottom: "10px" }}
          />
        )}
        <label
          style={{
            display: "block",
            border: "1px dashed rgba(255,255,255,0.3)",
            borderRadius: "8px",
            padding: "16px",
            textAlign: "center",
            cursor: "pointer",
            marginBottom: "18px",
            color: "#B8B0C8",
          }}
        >
          {photoFile ? photoFile.name : "+ Add a photo"}
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => handlePhotoSelect(e.target.files[0])}
          />
        </label>

        <button
          type="submit"
          disabled={sending || !selectedBarId || !message.trim()}
          style={{
            background: sent ? "#4ADE80" : "linear-gradient(90deg, #6D28D9, #A855F7)",
            border: "none",
            padding: "14px",
            borderRadius: "8px",
            color: "#fff",
            fontWeight: 600,
            fontSize: "15px",
            cursor: "pointer",
            width: "100%",
          }}
        >
          {sending ? "Sending..." : sent ? "✓ Sent!" : "Send Update"}
        </button>

        {error && <p style={{ color: "#F472B6", marginTop: "12px" }}>{error}</p>}
      </form>

      <TextCustomers bars={myBars} />

      <h3 style={{ marginBottom: "10px" }}>Recent Updates</h3>
      {loading ? (
        <p style={{ color: "#B8B0C8" }}>Loading...</p>
      ) : history.length === 0 ? (
        <p style={{ color: "#B8B0C8" }}>No updates sent yet.</p>
      ) : (
        history.map((item) => (
          <div
            key={item.id}
            style={{
              background: "#0D0B1A",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "10px",
              padding: "15px",
              marginBottom: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>{item.message}</p>
              <p style={{ margin: "4px 0 0", color: "#B8B0C8", fontSize: "16px" }}>
                {barName(item.barId)} · {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : ""}
              </p>
            </div>
            <button
              onClick={() => handleDelete(item.id)}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "8px",
                color: "#F472B6",
                padding: "8px 12px",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              Delete
            </button>
          </div>
        ))
      )}
    </div>
  );
}